import { useCallback, useEffect, useState } from 'react';
import { LotAPI, NFTI } from '@tacans/shared-marketplace-layer';

import transactionsRegistry from 'src/services/transactions/transactions-registry';
import { ITransaction, ITransactionStatusE } from 'src/services/transactions/types';
import { useIsMounted } from 'src/hooks/useIsMounted';
import { getLotType } from 'src/utils/lot.utils';

interface TokenTransactionsListenerI {
    collectible?: NFTI;
    onTransaction?: (contractMethod: string, transaction: ITransaction) => void;
}

export const useTokenTransactionsListener = ({
    collectible,
    onTransaction,
}: TokenTransactionsListenerI) => {
    const [priceStatus, setPriceStatus] = useState<Record<string, boolean>>({});
    const isMounted = useIsMounted();

    const handleSellTransaction = useCallback(async (transaction: ITransaction) => {
        const { id, minNanoBidStep, minNanoBid, nanoPrice, type, copies } = transaction.lot;
        const { isAuction } = getLotType(type);
        const isPending = transaction.status === ITransactionStatusE.Pending;

        const newPrice = isAuction
            ? +minNanoBid!
            : +nanoPrice;

        // if copies -> get price for 1 edition
        // if minBid -> auction -> pass bid and bidStep
        if (!priceStatus[transaction.txHash] && isPending) {
            await LotAPI.changePrice({
                id,
                txHash: transaction.txHash,
                ...(isAuction ? {
                    minNanoBid: copies ? (newPrice / +copies) : newPrice,
                    minNanoBidStep: +minNanoBidStep!,
                } : {
                    nanoPrice: newPrice,
                })
            });
            isMounted(() => {
                setPriceStatus(prev => ({
                    ...prev,
                    [transaction.txHash]: true,
                }));
            });
        }
        onTransaction?.(transaction.contractMethod, transaction);
    }, [onTransaction, priceStatus, isMounted]);

    const handleTransactions = useCallback((transactions: ITransaction[]) => {
        const transactionsByToken = transactions.filter(tr => (
            tr.token.id === collectible?.id
        ));
        transactionsByToken.forEach(transaction => {
            if (transaction.contractMethod === 'sell') {
                handleSellTransaction(transaction);
            }
        });
    }, [collectible?.id, handleSellTransaction]);

    useEffect(() => {
        if (collectible?.id) {
            transactionsRegistry.subscribe(handleTransactions);
        }
        return () => {
            transactionsRegistry.unsubscribe(handleTransactions);
        };
    }, [collectible?.id, handleTransactions]);
};
